import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  ChevronDown,
  CheckCircle2,
  Circle,
  Clock,
  Download,
  Info,
  Loader2,
  Lock,
  Plus,
  Save,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import {
  createDraftAssessment,
  getMyAssessment,
  listMyAssessments,
  saveAssessmentStep,
  submitAssessment,
} from "@/lib/visa-readiness/assessments.functions";
import { getMyCredits } from "@/lib/visa-readiness/credits.functions";
import { getVisaAccess } from "@/lib/visa-readiness/access.functions";
import {
  DEGREE_LEVELS,
  ENGLISH_TESTS,
  FUNDING_SOURCES,
  ITR_RANGES,
  POST_GRAD_INTENTS,
  type PartialForm,
} from "@/lib/visa-readiness/types";
import { teaserScore } from "@/lib/visa-readiness/scoring";
import { sectionConfidence, confidenceLabel, type SectionConfidence } from "@/lib/visa-readiness/section-confidence";
import { exportAssessmentPdf } from "@/lib/visa-readiness/export-pdf";

export const Route = createFileRoute("/visa-readiness/assessment")({
  head: () => ({
    meta: [
      { title: "Assessment Hub - AI Visa Readiness | WeLivdIt" },
      {
        name: "description",
        content:
          "Complete your WeLivdIt AI Visa Readiness Assessment at your own pace - academic, financial, work and refusal history sections with autosave.",
      },
      { property: "og:title", content: "Assessment Hub - AI Visa Readiness | WeLivdIt" },
      {
        property: "og:description",
        content: "Autosaved F-1 profile assessment covering academics, funding, work history and 214(b) risk factors.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AssessmentHub,
});

type SectionKey = "A" | "B" | "C" | "D";

const SECTIONS: Array<{ key: SectionKey; title: string; blurb: string; eta: string }> = [
  { key: "A", title: "General & Academic Profile", blurb: "Age, degree, GPA, English test, gaps", eta: "~2 min" },
  { key: "B", title: "Work Experience & Projects", blurb: "Internships, full-time, project highlights", eta: "~1 min" },
  { key: "C", title: "Financial & Sponsor Details", blurb: "Cost, funding mix, sponsor ITR, loan", eta: "~2 min" },
  { key: "D", title: "Travel, Refusals & US Family Ties", blurb: "Prior refusals, visas, ties, post-grad plan", eta: "~2 min" },
];

// Fields required per section (used for completion badge)
function isSectionComplete(key: SectionKey, form: PartialForm): boolean {
  const s1 = form.s1 ?? {};
  const s2 = form.s2 ?? {};
  const s3 = form.s3 ?? {};
  const s4 = form.s4 ?? {};
  const s5 = form.s5 ?? {};
  const s6 = form.s6 ?? {};
  const s7 = form.s7 ?? {};
  const s8 = form.s8 ?? {};
  if (key === "A") {
    return Boolean(s1.age && s2.degreeLevel && s2.currentCourse && s2.gpa != null && s2.englishTestType);
  }
  if (key === "B") {
    return Boolean(s3.projectsSummary && (s3.presentWorkType ?? "none"));
  }
  if (key === "C") {
    return Boolean(
      s4.universityName &&
        s4.program &&
        s4.whyThisUniversity &&
        s5.totalCostUsd &&
        (s5.fundingBreakdown?.length ?? 0) > 0,
    );
  }
  if (key === "D") {
    return Boolean(s6.ds160Confirmed !== undefined && s8.postGradPlan && s8.homeCountryTies && s8.postGradIntent);
  }
  return false;
}

function AssessmentHub() {
  const navigate = useNavigate();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [form, setForm] = useState<PartialForm>({});
  const [open, setOpen] = useState<Record<SectionKey, boolean>>({ A: true, B: false, C: false, D: false });
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedTick, setSavedTick] = useState(0);
  const dirty = useRef(false);

  const createDraft = useServerFn(createDraftAssessment);
  const saveStep = useServerFn(saveAssessmentStep);
  const submit = useServerFn(submitAssessment);
  const listMine = useServerFn(listMyAssessments);
  const getOne = useServerFn(getMyAssessment);
  const fetchCredits = useServerFn(getMyCredits);
  const fetchAccess = useServerFn(getVisaAccess);

  useEffect(() => {
    (async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) {
          setSignedIn(false);
          toast.message("Feature currently in private preview.", {
            description: "Please sign in with a whitelisted account.",
          });
          navigate({ to: "/auth", search: { redirect: "/visa-readiness", google: false } });
          return;
        }
        setSignedIn(true);

        // Private-preview gate.
        try {
          const access = await fetchAccess();
          if (!access.allowed) {
            toast.message("Feature currently in private preview.", {
              description: "Request access from the WeLivdIt team to try the Visa Readiness Assessment.",
            });
            navigate({ to: "/student-dashboard" });
            return;
          }
        } catch {
          navigate({ to: "/student-dashboard" });
          return;
        }

        let creditsRemaining = 0;
        try {
          const c = await fetchCredits();
          creditsRemaining = c.credits_remaining ?? 0;
          setCredits(creditsRemaining);
        } catch {
          setCredits(0);
        }

        let existingDraft: { id: string; form_data: unknown } | null = null;
        try {
          const mine = await listMine();
          const draft = mine.find((x) => x.status === "draft");
          if (draft) {
            const detail = await getOne({ data: { id: draft.id } });
            if (detail) existingDraft = detail;
          }
        } catch {
          /* noop */
        }

        if (existingDraft) {
          setAssessmentId(existingDraft.id);
          setForm(((existingDraft.form_data as unknown) as PartialForm) ?? {});
          setLastSavedAt(Date.now());
        } else if (creditsRemaining > 0) {
          try {
            const created = await createDraft();
            setAssessmentId(created.id);
            setLastSavedAt(Date.now());
          } catch (err) {
            toast.error(err instanceof Error ? err.message : "Could not start assessment.");
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [createDraft, fetchCredits, getOne, listMine]);

  useEffect(() => {
    if (!assessmentId || !dirty.current) return;
    const t = setTimeout(async () => {
      try {
        setSaving(true);
        await saveStep({ data: { id: assessmentId, currentStep: 1, formData: form as Record<string, unknown> } });
        dirty.current = false;
        setLastSavedAt(Date.now());
        setSaveError(null);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : "Could not save");
      } finally {
        setSaving(false);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [form, assessmentId, saveStep]);

  // Refresh relative "saved X ago" label each 30s.
  useEffect(() => {
    const t = setInterval(() => setSavedTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Warn on close/reload if unsaved changes are pending.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const setField = useCallback(<K extends keyof PartialForm>(key: K, value: PartialForm[K]) => {
    dirty.current = true;
    setForm((prev) => ({ ...prev, [key]: { ...(prev[key] as object), ...(value as object) } }));
  }, []);

  const completed = useMemo(
    () => SECTIONS.filter((s) => isSectionComplete(s.key, form)).length,
    [form],
  );
  const teaser = useMemo(() => teaserScore(form), [form]);
  const confidences = useMemo(() => {
    const map: Record<SectionKey, SectionConfidence> = {
      A: sectionConfidence("A", form),
      B: sectionConfidence("B", form),
      C: sectionConfidence("C", form),
      D: sectionConfidence("D", form),
    };
    return map;
  }, [form]);

  // savedTick is intentionally read to force re-render every 30s so the
  // "Saved just now" label re-computes.
  void savedTick;
  const savedLabel = useMemo(() => {
    if (saving) return "Saving…";
    if (saveError) return "Save failed - retrying";
    if (!lastSavedAt) return "Not saved yet";
    const diff = Math.max(0, Date.now() - lastSavedAt);
    if (diff < 45_000) return "Saved just now";
    const mins = Math.round(diff / 60_000);
    if (mins < 60) return `Saved ${mins} min ago`;
    const hrs = Math.round(mins / 60);
    return `Saved ${hrs} hr ago`;
  }, [saving, saveError, lastSavedAt, savedTick]);

  const handleExportPdf = () => {
    try {
      exportAssessmentPdf(form);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not export PDF");
    }
  };

  const toggle = (k: SectionKey) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const jumpNext = (current: SectionKey) => {
    const idx = SECTIONS.findIndex((s) => s.key === current);
    const next = SECTIONS[idx + 1];
    if (next) setOpen((o) => ({ ...o, [next.key]: true }));
  };

  const handleSubmit = async () => {
    if (!assessmentId) return;
    setSubmitting(true);
    try {
      const res = await submit({ data: { id: assessmentId, formData: form } });
      toast.success("Report ready");
      navigate({ to: "/visa-readiness/report/$id", params: { id: res.reportId } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (signedIn === false) {
    return (
      <div className="mx-auto max-w-md px-5 py-20 text-center">
        <h1 className="font-display text-2xl font-semibold">Sign in to continue</h1>
        <Button className="mt-4" onClick={() => navigate({ to: "/auth" })}>Sign in</Button>
      </div>
    );
  }
  if (credits !== null && credits <= 0 && !assessmentId) {
    return (
      <div className="mx-auto max-w-md px-5 py-20 text-center">
        <h1 className="font-display text-2xl font-semibold">You need a credit to start</h1>
        <Button className="mt-4" onClick={() => navigate({ to: "/visa-readiness" })}>See plans</Button>
      </div>
    );
  }

  const allDone = completed === SECTIONS.length;

  return (
    <div className="bg-background">
      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 md:grid-cols-[1fr_320px] md:px-6">
        <div className="min-w-0 space-y-5">
          {/* Header */}
          <div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-1">
                <Lock className="h-3 w-3" /> Encrypted 256-bit SSL
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-1">
                Data deleted after analysis
              </span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2.5 py-1",
                  saveError
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-700"
                    : saving
                      ? "bg-card"
                      : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700",
                )}
                aria-live="polite"
                title={saveError ?? undefined}
              >
                {saving ? (
                  <><Save className="h-3 w-3 animate-pulse" /> {savedLabel}</>
                ) : saveError ? (
                  <><AlertTriangle className="h-3 w-3" /> {savedLabel}</>
                ) : (
                  <><CheckCircle2 className="h-3 w-3" /> {savedLabel}</>
                )}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-display text-2xl font-semibold md:text-3xl">Visa Readiness Assessment</h1>
                <p className="mt-1 text-sm text-foreground/70">
                  Fill at your own pace. {completed} of {SECTIONS.length} sections complete.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleExportPdf}>
                <Download className="mr-2 h-4 w-4" /> Export PDF
              </Button>
            </div>
            <Progress value={(completed / SECTIONS.length) * 100} className="mt-3" />
          </div>

          {/* Auto-fill banner */}
          <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-4 md:flex-row md:items-center">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium">Fast-Track with AI</p>
                <p className="text-sm text-foreground/70">
                  Upload your DS-160 draft or resume and we&apos;ll auto-fill fields for you.
                </p>
              </div>
            </div>
            <div className="flex w-full gap-2 md:w-auto">
              <Button
                variant="secondary"
                className="flex-1 md:flex-none"
                onClick={() => toast.info("Coming soon - AI auto-fill will scan your DS-160 / resume.")}
              >
                <Upload className="mr-2 h-4 w-4" /> Upload
              </Button>
              <Button variant="ghost" className="flex-1 md:flex-none" onClick={() => setOpen({ A: true, B: false, C: false, D: false })}>
                Fill manually
              </Button>
            </div>
          </div>

          {/* Accordion sections */}
          <div className="space-y-3">
            {SECTIONS.map((sec) => {
              const done = isSectionComplete(sec.key, form);
              const isOpen = open[sec.key];
              return (
                <div key={sec.key} className="overflow-hidden rounded-2xl border bg-card">
                  <button
                    type="button"
                    onClick={() => toggle(sec.key)}
                    className="flex w-full items-center gap-3 p-4 text-left transition hover:bg-muted/40"
                  >
                    <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-full", done ? "bg-emerald-500/15 text-emerald-600" : "bg-muted text-muted-foreground")}>
                      {done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary">Section {sec.key}</span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                          <Clock className="h-3 w-3" /> {sec.eta}
                        </span>
                        {done && <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600">Complete</span>}
                      </span>
                      <span className="mt-0.5 block truncate font-display text-base font-semibold">{sec.title}</span>
                      <span className="block truncate text-xs text-foreground/60">{sec.blurb}</span>
                    </span>
                    <ChevronDown className={cn("h-5 w-5 shrink-0 transition-transform", isOpen && "rotate-180")} />
                  </button>
                  {isOpen && (
                    <div className="border-t bg-background/50 p-4 md:p-6">
                      {sec.key === "A" && <SectionA form={form} set={setField} />}
                      {sec.key === "B" && <SectionB form={form} set={setField} />}
                      {sec.key === "C" && <SectionC form={form} set={setField} />}
                      {sec.key === "D" && <SectionD form={form} set={setField} />}
                      <ConfidenceCard conf={confidences[sec.key]} />
                      <div className="mt-5 flex justify-end">
                        <Button variant="outline" onClick={() => jumpNext(sec.key)}>
                          {SECTIONS[SECTIONS.findIndex((s) => s.key === sec.key) + 1] ? "Save & continue" : "Done"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Submit */}
          <div className="rounded-2xl border bg-card p-5">
            <p className="text-sm text-foreground/70">
              When all sections are complete, submit for a full AI-generated readiness report.
            </p>
            <Button className="mt-3 w-full md:w-auto" size="lg" onClick={handleSubmit} disabled={submitting || !allDone}>
              {submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</>
              ) : allDone ? (
                "Submit & generate report"
              ) : (
                `Complete all sections to submit (${completed}/${SECTIONS.length})`
              )}
            </Button>
          </div>
        </div>

        {/* Sidebar - Readiness Index */}
        <aside className="md:sticky md:top-6 md:h-fit">
          <div className="rounded-2xl border bg-card p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live Visa Readiness Index</p>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-display text-5xl font-bold text-primary">{teaser.overall}</span>
              <span className="mb-1 text-sm text-foreground/60">/ 100</span>
            </div>
            <Progress value={teaser.overall} className="mt-2" />
            <p className="mt-3 text-xs text-foreground/60">
              {teaser.flagCount} early red flag{teaser.flagCount === 1 ? "" : "s"} detected. The full report explains each one.
            </p>

            <div className="mt-5 space-y-2">
              {SECTIONS.map((s) => {
                const done = isSectionComplete(s.key, form);
                return (
                  <div key={s.key} className="flex items-center gap-2 text-sm">
                    {done ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className={cn("truncate", done ? "text-foreground" : "text-foreground/60")}>{s.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-3 rounded-xl border bg-muted/40 p-3 text-xs text-foreground/70">
            <p className="flex items-center gap-1 font-medium text-foreground/80"><Info className="h-3.5 w-3.5" /> Why we ask</p>
            <p className="mt-1">
              Each field maps to a real Visa Officer evaluation point - financial coverage, ties, DS-160 consistency, and 214(b) risk.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------------- Reusable primitives ---------------- //

function ConfidenceCard({ conf }: { conf: SectionConfidence }) {
  const tone =
    conf.level === "solid"
      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-800"
      : conf.level === "partial"
        ? "border-amber-500/30 bg-amber-500/5 text-amber-800"
        : "border-rose-500/30 bg-rose-500/5 text-rose-800";
  const barColor =
    conf.level === "solid" ? "bg-emerald-500" : conf.level === "partial" ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className={cn("mt-6 rounded-xl border p-3", tone)}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold">{confidenceLabel(conf.level)}</p>
        <p className="text-xs opacity-80">
          {conf.filled}/{conf.total} key fields - {conf.percent}%
        </p>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
        <div className={cn("h-full transition-all", barColor)} style={{ width: `${conf.percent}%` }} />
      </div>
      {conf.missing.length > 0 && (
        <p className="mt-2 text-xs">
          <span className="font-medium">Still needed for AI:</span>{" "}
          {conf.missing.slice(0, 6).join(", ")}
          {conf.missing.length > 6 ? ` +${conf.missing.length - 6} more` : ""}
        </p>
      )}
      {conf.notes.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs">
          {conf.notes.map((n, i) => (
            <li key={i} className="flex items-start gap-1">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type Setter = <K extends keyof PartialForm>(key: K, value: PartialForm[K]) => void;

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && (
        <p className="mt-1.5 flex items-start gap-1 text-[11px] text-foreground/60">
          <span aria-hidden>💡</span>
          <span>{hint}</span>
        </p>
      )}
    </div>
  );
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>;
  value: T | null | undefined;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-full border px-3.5 py-1.5 text-sm transition",
              active
                ? "border-primary bg-primary text-primary-foreground shadow-sm"
                : "border-border bg-background hover:bg-muted",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function YesNo({
  value,
  onChange,
}: {
  value: boolean | null | undefined;
  onChange: (v: boolean) => void;
}) {
  return (
    <Segmented
      options={[
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ]}
      value={value == null ? undefined : value ? "yes" : "no"}
      onChange={(v) => onChange(v === "yes")}
    />
  );
}

// ---------------- Section A ---------------- //

function SectionA({ form, set }: { form: PartialForm; set: Setter }) {
  const s1 = form.s1 ?? {};
  const s2 = form.s2 ?? {};
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Age">
          <Input type="number" min={15} max={80} value={s1.age ?? ""} onChange={(e) => set("s1", { age: Number(e.target.value) })} />
        </Field>
        <Field label="Intended intake (e.g., Fall 2026)">
          <Input value={s1.intendedIntake ?? ""} onChange={(e) => set("s1", { intendedIntake: e.target.value })} maxLength={30} />
        </Field>
      </div>
      <Field label="Interview date (if scheduled)">
        <Input type="date" value={s1.interviewDate ?? ""} onChange={(e) => set("s1", { interviewDate: e.target.value || null })} />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Degree level">
          <Select value={s2.degreeLevel ?? undefined} onValueChange={(v) => set("s2", { degreeLevel: v as (typeof DEGREE_LEVELS)[number] })}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{DEGREE_LEVELS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Course">
          <Input value={s2.currentCourse ?? ""} onChange={(e) => set("s2", { currentCourse: e.target.value })} maxLength={120} placeholder="e.g., B.Tech CSE" />
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Field label="GPA">
          <Input type="number" step="0.01" value={s2.gpa ?? ""} onChange={(e) => set("s2", { gpa: Number(e.target.value) })} />
        </Field>
        <Field label="GPA scale">
          <Select value={s2.gpaScale ?? "10"} onValueChange={(v) => set("s2", { gpaScale: v as "4" | "10" })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="10">/ 10</SelectItem><SelectItem value="4">/ 4</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Number of backlogs">
          <Select value={String(s2.backlogs ?? 0)} onValueChange={(v) => set("s2", { backlogs: Number(v.replace("+", "")) })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["0", "1", "2", "3", "4+"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Study / career gap (years)" hint="Any gap of 12+ months usually needs a clean explanation.">
          <Select value={String(s2.gapYears ?? 0)} onValueChange={(v) => set("s2", { gapYears: Number(v.replace("+", "")) })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["0", "1", "2", "3+"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Is your US degree in the same field as before?" hint="Field change is fine - but needs a clear narrative for the VO.">
          <Segmented
            options={[
              { value: "yes", label: "Yes, same field" },
              { value: "no", label: "No / changing major" },
            ]}
            value={s2.courseRelevanceSameField == null ? undefined : s2.courseRelevanceSameField ? "yes" : "no"}
            onChange={(v) => set("s2", { courseRelevanceSameField: v === "yes" })}
          />
        </Field>
      </div>

      <Field label="English test">
        <Segmented
          options={ENGLISH_TESTS.map((t) => ({ value: t, label: t === "None" ? "None / Waived" : t }))}
          value={s2.englishTestType ?? undefined}
          onChange={(v) => set("s2", { englishTestType: v })}
        />
      </Field>
      {s2.englishTestType && s2.englishTestType !== "None" && (
        <Field label={`Enter ${s2.englishTestType} score`}>
          <Input value={s2.englishScore ?? ""} onChange={(e) => set("s2", { englishScore: e.target.value })} maxLength={50} placeholder={s2.englishTestType === "IELTS" ? "e.g., 7.5 overall" : "Overall score"} />
        </Field>
      )}

      <Field label="GRE / GMAT score (optional)">
        <Input value={s2.greScore ?? ""} onChange={(e) => set("s2", { greScore: e.target.value })} maxLength={50} />
      </Field>
    </div>
  );
}

// ---------------- Section B ---------------- //

function SectionB({ form, set }: { form: PartialForm; set: Setter }) {
  const s3 = form.s3 ?? {};
  const workType = s3.presentWorkType ?? "none";
  return (
    <div className="space-y-5">
      <Field label="Notable projects (2-3 sentences)" hint="Concrete outcomes beat buzzwords - stack, scale, result.">
        <Textarea rows={4} value={s3.projectsSummary ?? ""} onChange={(e) => set("s3", { projectsSummary: e.target.value })} maxLength={1500} />
      </Field>

      <Field label="Current work status">
        <Segmented
          options={[
            { value: "none", label: "None" },
            { value: "internship", label: "Internship" },
            { value: "fulltime", label: "Full-time / Work Experience" },
          ]}
          value={workType}
          onChange={(v) => set("s3", { presentWorkType: v })}
        />
      </Field>

      {workType === "internship" && (
        <div className="grid gap-4 rounded-xl border bg-muted/30 p-4 md:grid-cols-2">
          <Field label="Organization / Company">
            <Input value={s3.workOrg ?? ""} onChange={(e) => set("s3", { workOrg: e.target.value })} maxLength={200} />
          </Field>
          <Field label="Duration">
            <Input value={s3.workDuration ?? ""} onChange={(e) => set("s3", { workDuration: e.target.value })} maxLength={100} placeholder="e.g., 3 months" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Key duties & responsibilities">
              <Textarea rows={3} value={s3.workDuties ?? ""} onChange={(e) => set("s3", { workDuties: e.target.value })} maxLength={1500} />
            </Field>
          </div>
        </div>
      )}
      {workType === "fulltime" && (
        <div className="grid gap-4 rounded-xl border bg-muted/30 p-4 md:grid-cols-2">
          <Field label="Company name">
            <Input value={s3.workOrg ?? ""} onChange={(e) => set("s3", { workOrg: e.target.value })} maxLength={200} />
          </Field>
          <Field label="Role / title">
            <Input value={s3.workRole ?? ""} onChange={(e) => set("s3", { workRole: e.target.value })} maxLength={200} />
          </Field>
          <Field label="Total experience / duration">
            <Input value={s3.workDuration ?? ""} onChange={(e) => set("s3", { workDuration: e.target.value })} maxLength={100} placeholder="e.g., 2 years" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Key duties & responsibilities">
              <Textarea rows={3} value={s3.workDuties ?? ""} onChange={(e) => set("s3", { workDuties: e.target.value })} maxLength={1500} />
            </Field>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Total internship months">
          <Input type="number" min={0} value={s3.internshipsMonths ?? 0} onChange={(e) => set("s3", { internshipsMonths: Number(e.target.value) })} />
        </Field>
        <Field label="Total full-time months">
          <Input type="number" min={0} value={s3.fullTimeMonths ?? 0} onChange={(e) => set("s3", { fullTimeMonths: Number(e.target.value) })} />
        </Field>
      </div>
    </div>
  );
}

// ---------------- Section C ---------------- //

const FUNDING_LABEL: Record<(typeof FUNDING_SOURCES)[number], string> = {
  loan: "Sanctioned Education Loan",
  savings: "Savings / Fixed Deposits",
  sponsor: "Sponsor Income",
  scholarship: "Scholarship",
};

function SectionC({ form, set }: { form: PartialForm; set: Setter }) {
  const s4 = form.s4 ?? {};
  const s5 = form.s5 ?? {};
  const breakdown = s5.fundingBreakdown ?? [];
  const toggleFunding = (key: (typeof FUNDING_SOURCES)[number]) => {
    const next = breakdown.includes(key) ? breakdown.filter((x) => x !== key) : [...breakdown, key];
    set("s5", { fundingBreakdown: next });
  };
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="University name">
          <Input value={s4.universityName ?? ""} onChange={(e) => set("s4", { universityName: e.target.value })} maxLength={200} />
        </Field>
        <Field label="Program">
          <Input value={s4.program ?? ""} onChange={(e) => set("s4", { program: e.target.value })} maxLength={200} />
        </Field>
      </div>
      <Field label="US News ranking (optional)">
        <Input value={s4.usRanking ?? ""} onChange={(e) => set("s4", { usRanking: e.target.value })} maxLength={50} />
      </Field>
      <Field label="Why this university?" hint="Faculty, research, curriculum, career outcomes - in your own words.">
        <Textarea rows={4} value={s4.whyThisUniversity ?? ""} onChange={(e) => set("s4", { whyThisUniversity: e.target.value })} maxLength={1500} />
      </Field>

      <Field label="Total first-year cost (USD)" hint="Tuition + living. Match this to your financial docs.">
        <Input type="number" value={s5.totalCostUsd ?? ""} onChange={(e) => set("s5", { totalCostUsd: Number(e.target.value) })} />
      </Field>

      <Field label="Funding source breakdown" hint="Pick every source you plan to use.">
        <div className="grid gap-2 md:grid-cols-2">
          {FUNDING_SOURCES.map((k) => {
            const active = breakdown.includes(k);
            return (
              <label key={k} className={cn("flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition", active ? "border-primary bg-primary/5" : "bg-background hover:bg-muted/40")}>
                <Checkbox checked={active} onCheckedChange={() => toggleFunding(k)} />
                <span>{FUNDING_LABEL[k]}</span>
              </label>
            );
          })}
        </div>
      </Field>

      {breakdown.includes("loan") && (
        <div className="grid gap-4 rounded-xl border bg-muted/30 p-4 md:grid-cols-2">
          <Field label="Loan type">
            <Segmented
              options={[
                { value: "secured", label: "Secured" },
                { value: "unsecured", label: "Unsecured" },
              ]}
              value={s5.loanType ?? undefined}
              onChange={(v) => set("s5", { loanType: v })}
            />
          </Field>
          <Field label="Loan amount (INR)">
            <Input type="number" value={s5.loanAmountInr ?? ""} onChange={(e) => set("s5", { loanAmountInr: Number(e.target.value) })} />
          </Field>
        </div>
      )}

      {breakdown.includes("sponsor") && (
        <div className="grid gap-4 rounded-xl border bg-muted/30 p-4 md:grid-cols-2">
          <Field label="Sponsor relation">
            <Input value={s5.sponsorRelation ?? ""} onChange={(e) => set("s5", { sponsorRelation: e.target.value })} maxLength={150} placeholder="e.g., Father" />
          </Field>
          <Field label="Sponsor annual income (INR)">
            <Input type="number" value={s5.sponsorAnnualIncomeInr ?? ""} onChange={(e) => set("s5", { sponsorAnnualIncomeInr: Number(e.target.value) })} />
          </Field>
          <Field label="Does your sponsor file ITR?">
            <YesNo value={s5.sponsorFilesItr} onChange={(v) => set("s5", { sponsorFilesItr: v })} />
          </Field>
          {s5.sponsorFilesItr && (
            <Field label="ITR amount filed">
              <Select value={s5.sponsorItrRange ?? undefined} onValueChange={(v) => set("s5", { sponsorItrRange: v as (typeof ITR_RANGES)[number] })}>
                <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5-10L">5L - 10L</SelectItem>
                  <SelectItem value="10-15L">10L - 15L</SelectItem>
                  <SelectItem value="15-20L">15L - 20L</SelectItem>
                  <SelectItem value="20L+">More than 20L</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
      )}

      {breakdown.includes("savings") && (
        <Field label="Liquid funds / FDs (INR)">
          <Input type="number" value={s5.liquidFundsInr ?? ""} onChange={(e) => set("s5", { liquidFundsInr: Number(e.target.value) })} />
        </Field>
      )}

      <Field label="Anything to add about your funding plan? (optional)">
        <Textarea rows={2} value={s5.fundingSources ?? ""} onChange={(e) => set("s5", { fundingSources: e.target.value })} maxLength={1000} />
      </Field>
    </div>
  );
}

// ---------------- Section D ---------------- //

type Member = { relation: string; location?: string | null; status?: string | null };

const POST_GRAD_LABEL: Record<(typeof POST_GRAD_INTENTS)[number], string> = {
  return_home_employment: "Return to home country immediately for employment",
  opt_then_return: "Work on OPT to gain experience, then return",
  family_business: "Join / take over family business",
  research_phd: "Pursue further research / PhD",
};

function SectionD({ form, set }: { form: PartialForm; set: Setter }) {
  const s6 = form.s6 ?? {};
  const s7 = form.s7 ?? {};
  const s8 = form.s8 ?? {};
  const family: Member[] = (s8.familyMembers as Member[] | null | undefined) ?? [];
  const distant: Member[] = (s8.distantRelatives as Member[] | null | undefined) ?? [];

  const updateFamily = (idx: number, patch: Partial<Member>) => {
    const next = family.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    set("s8", { familyMembers: next });
  };
  const addFamily = () => set("s8", { familyMembers: [...family, { relation: "", location: "", status: "" }] });
  const removeFamily = (idx: number) => set("s8", { familyMembers: family.filter((_, i) => i !== idx) });

  const updateDistant = (idx: number, patch: Partial<Member>) => {
    const next = distant.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    set("s8", { distantRelatives: next });
  };
  const addDistant = () => set("s8", { distantRelatives: [...distant, { relation: "", location: "" }] });
  const removeDistant = (idx: number) => set("s8", { distantRelatives: distant.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-6">
      {/* DS-160 */}
      <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
        <p className="font-medium">DS-160 consistency</p>
        <CheckboxRow label="I have submitted my DS-160" checked={!!s6.ds160Confirmed} onChange={(v) => set("s6", { ds160Confirmed: v })} />
        <CheckboxRow label="DS-160 income matches my sponsor's income" checked={s6.ds160IncomeMatchesSponsor !== false} onChange={(v) => set("s6", { ds160IncomeMatchesSponsor: v })} />
        <CheckboxRow label="DS-160 work history is accurate & complete" checked={s6.ds160WorkHistoryAccurate !== false} onChange={(v) => set("s6", { ds160WorkHistoryAccurate: v })} />
        <CheckboxRow label="Previous US travel is listed correctly" checked={s6.ds160PreviousUsTravelListed !== false} onChange={(v) => set("s6", { ds160PreviousUsTravelListed: v })} />
        <Field label="Anything else on your DS-160 you're unsure about?">
          <Textarea rows={2} value={s6.ds160Notes ?? ""} onChange={(e) => set("s6", { ds160Notes: e.target.value })} maxLength={1000} />
        </Field>
      </div>

      {/* Refusals */}
      <Field label="Have you been refused a US visa before?">
        <YesNo value={s7.previousUsRefusal} onChange={(v) => set("s7", { previousUsRefusal: v })} />
      </Field>
      {s7.previousUsRefusal && (
        <div className="grid gap-4 rounded-xl border bg-muted/30 p-4 md:grid-cols-2">
          <Field label="Number of refusals">
            <Segmented
              options={[
                { value: "1", label: "1 time" },
                { value: "more_than_1", label: "More than 1" },
              ]}
              value={s7.refusalCountBand ?? undefined}
              onChange={(v) => set("s7", { refusalCountBand: v, refusalCount: v === "1" ? 1 : 2 })}
            />
          </Field>
          <Field label="Year of last refusal">
            <Input value={s7.refusalYear ?? ""} onChange={(e) => set("s7", { refusalYear: e.target.value })} maxLength={20} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Refused university name">
              <Input value={s7.refusedUniversity ?? ""} onChange={(e) => set("s7", { refusedUniversity: e.target.value })} maxLength={200} />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Reason for rejection (if known)">
              <Textarea rows={3} value={s7.refusalReasonKnown ?? ""} onChange={(e) => set("s7", { refusalReasonKnown: e.target.value })} maxLength={1500} />
            </Field>
          </div>
        </div>
      )}

      {/* Other US visa */}
      <Field label="Do you have any other US visa?">
        <YesNo value={s7.hasOtherUsVisa} onChange={(v) => set("s7", { hasOtherUsVisa: v })} />
      </Field>
      {s7.hasOtherUsVisa && (
        <Field label="Which visa? (type / details)">
          <Input value={s7.otherVisasHeld ?? ""} onChange={(e) => set("s7", { otherVisasHeld: e.target.value })} maxLength={500} placeholder="e.g., B1/B2 valid until 2028" />
        </Field>
      )}

      <Field label="International travel history">
        <Textarea rows={2} value={s7.travelHistory ?? ""} onChange={(e) => set("s7", { travelHistory: e.target.value })} maxLength={1000} placeholder="Countries and years" />
      </Field>

      {/* Immediate family */}
      <Field label="Do you have immediate family in the US?" hint="Parents, siblings, or spouse.">
        <YesNo value={s8.familyInUs} onChange={(v) => set("s8", { familyInUs: v })} />
      </Field>
      {s8.familyInUs && (
        <div className="space-y-3 rounded-xl border bg-muted/30 p-4">
          {family.length === 0 && (
            <p className="text-xs text-foreground/60">Add each immediate family member below.</p>
          )}
          {family.map((m, i) => (
            <div key={i} className="grid gap-3 rounded-lg border bg-background p-3 md:grid-cols-3">
              <Field label="Relation">
                <Input value={m.relation} onChange={(e) => updateFamily(i, { relation: e.target.value })} placeholder="e.g., Uncle" />
              </Field>
              <Field label="Location / state">
                <Input value={m.location ?? ""} onChange={(e) => updateFamily(i, { location: e.target.value })} placeholder="e.g., New Jersey" />
              </Field>
              <Field label="Visa / legal status">
                <Input value={m.status ?? ""} onChange={(e) => updateFamily(i, { status: e.target.value })} placeholder="e.g., Green Card" />
              </Field>
              <div className="md:col-span-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => removeFamily(i)}>
                  <Trash2 className="mr-1 h-4 w-4" /> Remove
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addFamily}>
            <Plus className="mr-1 h-4 w-4" /> Add family member
          </Button>
        </div>
      )}

      {/* Distant relatives */}
      <Field label="Do you have other (distant) relatives in the US?">
        <YesNo value={s8.hasDistantRelatives} onChange={(v) => set("s8", { hasDistantRelatives: v })} />
      </Field>
      {s8.hasDistantRelatives && (
        <div className="space-y-3 rounded-xl border bg-muted/30 p-4">
          {distant.map((m, i) => (
            <div key={i} className="grid gap-3 rounded-lg border bg-background p-3 md:grid-cols-2">
              <Field label="Relation">
                <Input value={m.relation} onChange={(e) => updateDistant(i, { relation: e.target.value })} placeholder="e.g., Cousin" />
              </Field>
              <Field label="Location">
                <Input value={m.location ?? ""} onChange={(e) => updateDistant(i, { location: e.target.value })} placeholder="e.g., Texas" />
              </Field>
              <div className="md:col-span-2 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => removeDistant(i)}>
                  <Trash2 className="mr-1 h-4 w-4" /> Remove
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addDistant}>
            <Plus className="mr-1 h-4 w-4" /> Add relative
          </Button>
        </div>
      )}

      {/* Post-grad + ties */}
      <Field label="English comfort in interviews">
        <Segmented
          options={[
            { value: "low", label: "Low - I struggle under pressure" },
            { value: "medium", label: "Medium - comfortable but nervous" },
            { value: "high", label: "High - I speak fluently" },
          ]}
          value={s8.englishComfort ?? "medium"}
          onChange={(v) => set("s8", { englishComfort: v as "low" | "medium" | "high" })}
        />
      </Field>

      <Field label="Primary plan post-graduation" hint="One clear answer beats a long, vague one.">
        <div className="grid gap-2">
          {POST_GRAD_INTENTS.map((k) => {
            const active = s8.postGradIntent === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => set("s8", { postGradIntent: k })}
                className={cn(
                  "rounded-lg border px-4 py-3 text-left text-sm transition",
                  active ? "border-primary bg-primary/5" : "bg-background hover:bg-muted/40",
                )}
              >
                {POST_GRAD_LABEL[k]}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Post-graduation plan (be specific)">
        <Textarea rows={3} value={s8.postGradPlan ?? ""} onChange={(e) => set("s8", { postGradPlan: e.target.value })} maxLength={1500} placeholder="Industry, geography, why India, timeline…" />
      </Field>

      <Field label="Ties to your home country">
        <Textarea rows={3} value={s8.homeCountryTies ?? ""} onChange={(e) => set("s8", { homeCountryTies: e.target.value })} maxLength={1500} placeholder="Family, property, job offer, business…" />
      </Field>
    </div>
  );
}

function CheckboxRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-lg border bg-background p-3 text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <span>{label}</span>
    </label>
  );
}
